/**
 * The three questions asked before recorded hours become an invoice.
 *
 * Until this dialog the button on the time tab posted an empty body and the
 * route's defaults decided everything: one line per task, no expenses, the
 * configured VAT rate. Those are good defaults and they are still the defaults
 * here - the dialog opens on exactly the invoice the old button would have
 * written, so nobody has to answer anything to get what they used to get. What
 * it adds is the ability to disagree with one of them at the only moment where
 * disagreeing is cheap. Afterwards the entries are stamped and read-only, and
 * the correction is a credit note.
 *
 * The call itself moved here with the dialog, and so did every refusal it can
 * answer with. That is the point of the dialog rather than a side effect of it:
 * `no_hourly_rate` and `no_billable_work` are both fixable, and both are worth
 * reading while the options that provoked them are still on screen - ticking
 * expenses is precisely what turns "nothing to bill" into a document on a
 * project whose hours are all non-billable. So a refusal leaves the dialog
 * open. Only success closes it.
 *
 * `no_billable_time` is matched beside `no_billable_work` for the reason it was
 * matched on the button before: the API and this bundle are two deployables, and
 * a tab loaded against the older API would otherwise fall through to "something
 * went wrong" for a refusal that has a sentence written for it.
 *
 * ## The VAT rate is a whole percent, and the field refuses anything else
 *
 * The route accepts a float and rounds it once, and the line-item snapshot the
 * invoice stores is `z.number().int()`. So 9.5 does not fail anywhere - it
 * becomes 10 on a document nobody is told about. The field therefore keeps the
 * Create button off until the number is whole, because the operator learning it
 * at the moment they type is the only place the half point can still be argued
 * about.
 *
 * An empty field is a third answer, not zero: it means "use the rate from the
 * invoicing settings", which is what the server does with an absent value. That
 * is said under the field in words, because a blank box and a zero-rate invoice
 * look identical and mean opposite things. The rate is read once on open so the
 * box normally shows the actual number; when that read fails the box stays empty
 * and the same sentence still describes what will happen.
 */
import { useEffect, useState } from "react";
import { Loader2, Receipt } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { invoiceProjectTime } from "../../../lib/projects";
import { getNumbering } from "../../../lib/sales";
import { useInvoicingVat } from "../../useInvoicingVat";
import { Btn, MenuField, Modal, useToast, type MenuOption } from "../../ui";

/** How the billed hours are laid out on the document. The route's own default
 *  is "task", and this dialog opens on it. */
type GroupBy = "task" | "single";

export function InvoiceTimeModal({
  projectId,
  onClose,
  onDone,
}: {
  projectId: string;
  onClose: () => void;
  onDone: (invoiceId: string) => void;
}) {
  const t = useT();
  const toast = useToast();
  const [groupBy, setGroupBy] = useState<GroupBy>("task");
  const [includeExpenses, setIncludeExpenses] = useState(false);
  const [tax, setTax] = useState("");
  const [busy, setBusy] = useState(false);
  const { vatEnabled } = useInvoicingVat();

  // The company's configured rate, so the field shows the number that is about
  // to be used rather than asking for it. Allowed to fail silently: an empty
  // box already means "the server's default", which is the same outcome, and
  // the sentence under the field says so either way.
  //
  // `effectiveTaxRatePct` and not the stored rate: with VAT switched off the
  // stored rate is what this company WOULD charge, and the invoice this dialog
  // is about to write must carry none.
  useEffect(() => {
    let alive = true;
    getNumbering()
      .then((cfg) => {
        if (alive) setTax(String(Math.round(cfg.effectiveTaxRatePct ?? cfg.defaultTaxRatePct)));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const groups: MenuOption<GroupBy>[] = [
    { value: "task", label: t("projects.groupByTask") },
    { value: "single", label: t("projects.groupBySingle") },
  ];

  // Empty is a valid answer and means the configured rate. Anything typed has
  // to be a whole percent in range - see the file comment.
  const rate = tax.trim();
  const rateNum = rate === "" ? null : Number(rate);
  const rateOk =
    rateNum === null || (Number.isInteger(rateNum) && rateNum >= 0 && rateNum <= 100);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !rateOk) return;

    setBusy(true);
    try {
      const r = await invoiceProjectTime(projectId, {
        groupBy,
        includeExpenses,
        // Omitted rather than sent as zero when the box is empty: the route
        // reads an absent field as "the configured rate".
        ...(rateNum === null ? {} : { taxRatePct: rateNum }),
      });
      toast(t("projects.invoicedTime", { number: r.invoice.number }));
      onDone(r.invoice.id);
      onClose();
    } catch (err) {
      // Every refusal this endpoint has is fixable by somebody, so each one says
      // what is missing rather than "something went wrong". 403 is here even
      // though the button that opens this dialog is hidden without the right:
      // the permission can be taken away while the tab is open, and a dialog
      // that fails anonymously on the way out is worse than one that never
      // appeared.
      const e2 = err as { status?: number; body?: { error?: string } };
      toast(
        e2.status === 403
          ? t("projects.noSalesRight")
          : // `no_billable_work` is what the endpoint answers since it learned
            // to put expenses on the same invoice: the refusal covers both
            // sources, so it got a name that does not promise it is only about
            // time. `no_billable_time` is the pre-wizard word, still matched
            // because a browser tab and the API do not deploy at one instant.
            e2.body?.error === "no_billable_work"
            ? t("projects.noBillableWork")
            : e2.body?.error === "no_billable_time"
              ? t("projects.noBillableTime")
              : e2.body?.error === "no_hourly_rate"
                ? t("projects.noHourlyRate")
                : // Somebody else billed part of this work between the click and
                  // the write. Nothing was created, so the honest instruction is
                  // to look again rather than to retry blindly.
                  e2.body?.error === "billed_concurrently"
                  ? t("projects.billedConcurrently")
                  : t("common.error"),
      );
    } finally {
      setBusy(false);
    }
  }

  const title = t("projects.invoiceTimeOptions");

  return (
    <Modal title={title} label={title} onClose={onClose} className="max-w-[460px]">
      <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
        <div>
          <span className="crm-lab">{t("projects.groupBy")}</span>
          <MenuField
            value={groupBy}
            onChange={setGroupBy}
            options={groups}
            label={t("projects.groupBy")}
            variant="form"
          />
        </div>

        {/* Hidden, not disabled, while the company charges no VAT: an empty box
            already sends nothing and the server already answers 0, so a greyed
            field would only be a question with one possible answer. */}
        {vatEnabled ? (
          <div>
            <span className="crm-lab">{t("projects.taxRate")}</span>
            <label className="crm-inp">
              <input
                type="number"
                step="1"
                min="0"
                max="100"
                inputMode="numeric"
                value={tax}
                onChange={(ev) => setTax(ev.target.value)}
                aria-label={t("projects.taxRate")}
                autoComplete="off"
              />
            </label>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              {rateOk ? t("projects.taxRateDefaultHint") : t("projects.taxRateWhole")}
            </p>
          </div>
        ) : null}

        {/* Off by default, because the operator pressed a button that says time
            and an expense they did not ask for would arrive on the document
            without ever having been named. The sentence under it is what the
            tick actually does, including the part that cannot be undone: the
            expenses it takes are stamped as billed. */}
        <div>
          <label className="crm-inp cursor-pointer">
            <input
              type="checkbox"
              checked={includeExpenses}
              onChange={(ev) => setIncludeExpenses(ev.target.checked)}
            />
            <span className="text-[13.5px]">{t("projects.includeExpenses")}</span>
          </label>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            {t("projects.includeExpensesHint")}
          </p>
        </div>

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !rateOk}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Receipt className="size-4" />}
            {t("projects.invoiceTime")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
